import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Button } from "../button";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import CheckboxGroupInput from "../inputs/CheckboxGroupInput";
import { Role } from "../../constants/common";
import { useTranslation } from "react-i18next";
import { GetAllRolesResponse } from "../../hooks/common/interface";

interface AssignRolesToProcessesCardProps {
  roles?: GetAllRolesResponse[];
  filteredRoles: {
    id: number;
    label: string;
  }[];
  setFilteredRoles: Dispatch<
    SetStateAction<
      {
        id: number;
        label: string;
      }[]
    >
  >;
  choosenRoles: {
    id: number;
    label: string;
  }[];
  setChoosenRoles: Dispatch<
    SetStateAction<
      {
        id: number;
        label: string;
      }[]
    >
  >;
}

const AssignRolesToProcessesCard = ({
  roles,
  filteredRoles,
  setFilteredRoles,
  choosenRoles,
  setChoosenRoles,
}: AssignRolesToProcessesCardProps) => {
  const { t, i18n } = useTranslation();

  const [isResetCheckboxes, setIsResetCheckboxes] = useState(false);

  const [selectedRoles, setSelectedRoles] = useState<Record<number, boolean>>(
    {}
  );

  useEffect(() => {
    if (!roles) return;

    // Compute roles based on the current language and available roles
    const computedRoles = [
      { id: 0, label: t("selectAll") },
      ...roles.map((item: Role) => ({
        id: item.n_RoleID,
        label: i18n.language === "en" ? item.s_RoleName : item.s_RoleNameAr,
      })),
    ];

    // Set the computed roles to the filteredRoles state
    setFilteredRoles(computedRoles);
  }, [i18n.language, roles, setFilteredRoles, t]);

  useEffect(() => {
    // Filter out choosen roles from filteredRoles except for the role with id = 0
    const filtered = filteredRoles.filter(
      (role) =>
        role.id === 0 || !choosenRoles.some((chosen) => chosen.id === role.id)
    );

    setFilteredRoles(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choosenRoles, setFilteredRoles]);

  const handleGroupChange = (
    selectedValues: Record<number, boolean>,
    isChoosenRoles: boolean
  ) => {
    setSelectedRoles(selectedValues);
  };

  const handleAdd = () => {
    // Add selected roles from Roles to Choosen Roles
    const selectedFromRoles = filteredRoles.filter(
      (role) => role.id !== 0 && selectedRoles[role.id]
    );

    setChoosenRoles((prev) => [
      ...prev,
      ...selectedFromRoles.filter(
        (role) => !prev.some((item) => item.id === role.id)
      ),
    ]);

    setSelectedRoles({}); // Uncheck all checkboxes
    setIsResetCheckboxes(true);
  };

  const handleAddAll = () => {
    // Add all roles except "Select All"
    const nonSelectAllRoles = filteredRoles.filter((role) => role.id !== 0);
    setChoosenRoles((prev) => [
      ...prev,
      ...nonSelectAllRoles.filter(
        (role) => !prev.some((item) => item.id === role.id)
      ),
    ]);

    setSelectedRoles({}); // Uncheck all checkboxes
    setIsResetCheckboxes(true);
  };

  const handleRemove = () => {
    // Remove selected roles from Choosen Roles and add them to Roles
    const selectedFromChoosen = choosenRoles.filter(
      (role) => role.id !== 0 && selectedRoles[role.id]
    );

    setFilteredRoles((prev) => [...prev, ...selectedFromChoosen]);

    setChoosenRoles((prev) =>
      prev.filter((role) => !selectedFromChoosen.some((r) => r.id === role.id))
    );

    setSelectedRoles({}); // Uncheck all checkboxes
    setIsResetCheckboxes(true);
  };

  const handleRemoveAll = () => {
    // Remove all roles from Choosen Roles except "Select All"
    setChoosenRoles((prev) => prev.filter((role) => role.id === 0));

    if (roles) {
      // Compute roles based on the current language and available roles
      const computedRoles = [
        { id: 0, label: t("selectAll") },
        ...roles.map((item: Role) => ({
          id: item.n_RoleID,
          label: i18n.language === "en" ? item.s_RoleName : item.s_RoleNameAr,
        })),
      ];

      // Set the computed roles to the filteredRoles state
      setFilteredRoles(computedRoles);
    }

    setSelectedRoles({}); // Uncheck all checkboxes
    setIsResetCheckboxes(true);
  };

  return (
    <div className="grid sm:grid-cols-3 gap-4 md:gap-2">
      <div className="flex flex-col gap-2">
        <p>{t("roles")}</p>
        <CheckboxGroupInput
          id="roles"
          options={filteredRoles}
          onChange={(selectedValues) =>
            handleGroupChange(selectedValues, false)
          }
          isResetCheckboxes={isResetCheckboxes}
          setIsResetCheckboxes={setIsResetCheckboxes}
        />
      </div>

      <div className="flex flex-col gap-5 items-center justify-center sm:mt-8 ">
        <div className="flex flex-col gap-1">
          <Button
            IconRight={
              i18n.language === "en" ? (
                <ChevronRight size={20} />
              ) : (
                <ChevronLeft size={20} />
              )
            }
            className="min-w-[180px]"
            onClick={handleAdd}
          >
            {t("add")}
          </Button>
          <Button
            IconRight={
              i18n.language === "en" ? (
                <ChevronsRight size={20} />
              ) : (
                <ChevronsLeft size={20} />
              )
            }
            className="min-w-[180px]"
            onClick={handleAddAll}
          >
            {t("addAll")}
          </Button>
        </div>

        <div className="flex flex-col gap-1">
          <Button
            Icon={
              i18n.language === "en" ? (
                <ChevronLeft size={20} />
              ) : (
                <ChevronRight size={20} />
              )
            }
            className="min-w-[180px]"
            onClick={handleRemove}
          >
            {t("remove")}
          </Button>
          <Button
            Icon={
              i18n.language === "en" ? (
                <ChevronsLeft size={20} />
              ) : (
                <ChevronsRight size={20} />
              )
            }
            className="min-w-[180px]"
            onClick={handleRemoveAll}
          >
            {t("removeAll")}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p>{t("choosenRoles")}</p>
        <CheckboxGroupInput
          id="choosenRoles"
          options={choosenRoles}
          onChange={(selectedValues) => handleGroupChange(selectedValues, true)}
          isResetCheckboxes={isResetCheckboxes}
          setIsResetCheckboxes={setIsResetCheckboxes}
        />
      </div>
    </div>
  );
};

export default React.memo(AssignRolesToProcessesCard);
